import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getStoredLanguage, isSpainVisitor, setStoredLanguage } from "@/lib/region";

/**
 * Sends logged-out visitors who appear to be in Spain to the Spanish
 * landing page (`/es`). Runs once per page load on `/` only.
 *
 * Escape hatches:
 *   • `/?lang=en` — stay on the English site and remember that choice
 *   • an explicit English choice saved from Settings or a previous `?lang=en`
 * Search engines pre-render `/` server-side and never hit this redirect.
 */
export function SpainLandingRedirect({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { i18n } = useTranslation();

  const params = new URLSearchParams(location.search);
  const forcedLang = params.get("lang");

  if (forcedLang === "en") {
    setStoredLanguage("en-US");
    if (i18n.language !== "en-US") void i18n.changeLanguage("en-US");
    return <>{children}</>;
  }

  if (getStoredLanguage() === "en-US") return <>{children}</>;

  if (isSpainVisitor()) {
    return <Navigate to={{ pathname: "/es", search: location.search }} replace />;
  }

  return <>{children}</>;
}
